/**
 * A modal dialog is painted in the browser's top layer, which stands above the
 * whole page however high anything on the page is stacked — so a toast raised
 * while a dialog is open goes up behind it, and no z-index can lift it out.
 *
 * The only way onto that layer is to be promoted to it, so the toast container
 * is made a popover: promoted as soon as it exists, and promoted again as each
 * toast arrives, since the layer is ordered by promotion and whatever was
 * promoted last stands on top of it.
 *
 * Manual, because a popover that dismissed itself would take the Escape key and
 * the next click outside away from the dialog underneath.
 */
export function raiseToastsToTopLayer(container: HTMLElement): void {
  if (typeof container.showPopover !== 'function') return;

  container.setAttribute('popover', 'manual');

  let observer: MutationObserver | null = null;

  const promote = (): void => {
    try {
      // Order of promotion is the whole mechanism, so it has to come off the
      // layer before it can go back on above what joined it since.
      if (container.matches(':popover-open')) container.hidePopover();
      container.showPopover();
    } catch {
      // A popover that will not open is display:none, and would take the toasts
      // with it — so the attribute goes and they stay where they were.
      container.removeAttribute('popover');
      observer?.disconnect();
    }
  };

  observer = new MutationObserver((records) => {
    if (records.some((record) => record.addedNodes.length > 0)) promote();
  });
  observer.observe(container, { childList: true, subtree: true });

  promote();
}
